/**
 * Client-side accessors for the current page context (COM-585).
 *
 * `page-context.ts` is deliberately pure — no `window` — so the navigation
 * rules unit-test under the node environment and the SSR inline script can
 * share them. This module is the one place that touches the live singleton,
 * so every consumer (the dataLayer seam, the Segment page call, the Segment
 * track trailer) reads the same value the same way.
 *
 * The singleton is seeded by `PageTypeInlineScript` at first paint and kept in
 * step by the committed-route publisher, so it is populated before any
 * post-hydration event can fire.
 */
import type { PageContext } from "./page-context";

/**
 * The context for the route currently being viewed, or `undefined` when the
 * contract is suppressed for this request — flag off, site not allowlisted,
 * bot, or the Builder editor. Every consumer must treat `undefined` as "add
 * nothing", never as an error.
 */
export function getCurrentPageContext(): PageContext | undefined {
  if (typeof window === "undefined") return undefined;
  return window.__HEC_PAGE_CTX;
}

/**
 * The current `hec_navigation_id`, or `undefined` when suppressed.
 *
 * This is the join key on every secondary surface. Segment carries ONLY this —
 * the full context lives once, on the dataLayer, and BigQuery joins to it.
 * Copying the whole payload onto every transport would recreate exactly the
 * duplication this ticket removes.
 */
export function getCurrentNavigationId(): string | undefined {
  return getCurrentPageContext()?.hec_navigation_id;
}

/**
 * `{ hec_navigation_id }` when a context exists, otherwise `{}` — for
 * spreading into an existing payload without disturbing it.
 *
 * Returning an empty object rather than `{ hec_navigation_id: undefined }`
 * matters: Segment serializes an explicit `undefined` as a present-but-null
 * property, which would show up as a payload change on every event even while
 * the feature is off.
 */
export function navigationIdProperties(): { hec_navigation_id?: string } {
  const navigationId = getCurrentNavigationId();
  return navigationId ? { hec_navigation_id: navigationId } : {};
}
